// ============================================================
// Counter.jsx — PERFORMANCE FIXED VERSION
// ============================================================
// CHANGES MADE:
//   1. REMOVED:  import CountUp, { useCountUp } from "react-countup"
//                → react-countup was causing 1,526ms CPU time on load
//                  and a forced reflow (layout recalculation). It's the
//                  single biggest performance problem on your page.
//
//   2. REMOVED:  The useCountUp({ ref: "counter", end: 1234567, ... })
//                call at the top of Counter() — it was running even
//                though nothing uses ref="counter" in your JSX.
//
//   3. ADDED:    useRafCount() — a tiny custom hook (zero dependencies)
//                that does exactly the same animated number count-up
//                using requestAnimationFrame. No extra npm package needed.
//
//   4. KEPT:     Everything else exactly the same — AOS, IntersectionObserver,
//                all icons, all layout, all CSS classes, all text.
// ============================================================

import { useEffect, useState, useRef } from "react";
import "./main.css";
import Icon1 from "../../assets/counter/Icon-1.webp";
import Icon2 from "../../assets/counter/completed-projects-2.svg";
import Icon3 from "../../assets/counter/Icon-3.webp";
import Icon6 from "../../assets/counter/substation-2.svg";
import Icon5 from "../../assets/counter/transmission-line-2.svg";
import Icon7 from "../../assets/counter/logo-33.webp";

// ✅ ADDED: Custom count-up hook — replaces react-countup entirely
// How it works:
//   - "end"     = the final number to count up to (e.g. 300)
//   - "enabled" = only starts animating when the section is visible on screen
//                 (we pass the existing "isVisible" state to this)
//   - Uses requestAnimationFrame so it's smooth and non-blocking
//   - Ease-out cubic curve: starts fast, slows down at the end (same feel as countup.js)
//   - Automatically cleans up when component unmounts
function useRafCount(end, enabled) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!enabled) return; // don't start until section is visible

    const duration = 2000; // 2 seconds to count up, same as before
    const start = performance.now();
    let raf;

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      setVal(Math.round(eased * end));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf); // cleanup on unmount
  }, [end, enabled]);

  return val;
}

function Counter() {
  // ❌ REMOVED: useCountUp({ ref: "counter", end: 1234567, ... })
  // This was running on every page load even though ref="counter"
  // didn't exist in the JSX. Dead code causing real CPU cost.

  useEffect(() => {
    let aosInstance;
    import("aos").then(({ default: AOS }) => {
      import("aos/dist/aos.css");
      AOS.init({ duration: 1000, once: false });
      aosInstance = AOS;
    });
    return () => {
      if (aosInstance) aosInstance.refresh();
    };
  }, []);

  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const targetRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      }
    );

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, [hasAnimated]);

  // ✅ ADDED: Call useRafCount for each counter value.
  // "isVisible" is passed as the trigger — counting only starts
  // when the user scrolls to the section, same behaviour as before.
  const count100  = useRafCount(100, isVisible);
  const count300  = useRafCount(300, isVisible);
  const count40   = useRafCount(40,  isVisible);
  const count8    = useRafCount(8,   isVisible);
  const count526  = useRafCount(526, isVisible);
  const count78   = useRafCount(78,  isVisible);

  return (
    <section className="Counter height-section">
      <div className="container">
        <div className="row ">
          <div
            className={`AnimatedCounter col-sm-12 ${isVisible ? "animate" : ""}`}
            ref={targetRef}
          >
            <div className="row">
              <div className="col-lg-3 col-md-3 col-sm-12">
                <div className="CounterNumber" data-aos="fade-down" data-aos-delay="200">
                  {/* ✅ CHANGED: was <CountUp end={100} enableScrollSpy />+  */}
                  <h1>{count100}+</h1>
                  <hr />
                  <div className="CouterText">
                    <p>Delighted<br />Client</p>
                    <img src={Icon1} alt="Ideas-img" />
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-3 col-sm-12">
                <div className="CounterNumber" data-aos="fade-down" data-aos-delay="206">
                  {/* ✅ CHANGED: was <CountUp end={300} enableScrollSpy />+  */}
                  <h1>{count300}+</h1>
                  <hr />
                  <div className="CouterText">
                    <p>Completed <br />Projects</p>
                    <img src={Icon2} alt="Ideas-img" />
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-3 col-sm-12">
                <div className="CounterNumber" data-aos="fade-down" data-aos-delay="208">
                  {/* ✅ CHANGED: was <CountUp end={40} enableScrollSpy />+  */}
                  <h1>{count40}+</h1>
                  <hr />
                  <div className="CouterText">
                    <p>Live <br />Projects</p>
                    <img src={Icon7} alt="Ideas-img" />
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-2">
              <div className="col-lg-3 col-md-3 col-sm-12">
                <div className="CounterNumber" data-aos="fade-down" data-aos-delay="206">
                  {/* ✅ CHANGED: was <CountUp end={8} enableScrollSpy />+  */}
                  <h1>{count8}+</h1>
                  <hr />
                  <div className="CouterText">
                    <p>Overseas <br />Projects</p>
                    <img src={Icon3} alt="Ideas-img" />
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-3 col-sm-12">
                <div className="CounterNumber" data-aos="fade-down" data-aos-delay="208">
                  {/* ✅ CHANGED: was <CountUp end={526} enableScrollSpy /> CKM  */}
                  <h1>{count526} CKM</h1>
                  <hr />
                  <div className="CouterText">
                    <p>Transmission <br />Line</p>
                    <img src={Icon5} alt="Ideas-img" />
                  </div>
                </div>
              </div>

              <div className="col-lg-3 col-md-3 col-sm-12">
                <div className="CounterNumber" data-aos="fade-down" data-aos-delay="210">
                  {/* ✅ CHANGED: was <CountUp end={78} enableScrollSpy />+  */}
                  <h1>{count78}+</h1>
                  <hr />
                  <div className="CouterText">
                    <p>Substation <br />Projects</p>
                    <img src={Icon6} alt="Ideas-img" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={`TextCounter  ${isVisible ? "animate" : ""}`} ref={targetRef}>
            <div data-aos="fade-right">
              <h2>Company Overview</h2>
              <p>
                IEEL & IDEAS ELECTRICALS & ENGINEERS LIMITED is a trusted
                leader in the Engineering, Procurement & Construction (EPC)
                industry, with a specialized focus on power and power-related
                infrastructure projects. Backed by over 20 years of experience,
                we operate across multiple verticals, delivering end-to-end
                project solutions with precision and excellence.
              </p>
              <p>
                Led by a team of seasoned technocrats, we have earned the
                confidence of a diverse client base that includes MNCs, large
                corporates, and government agencies. Our proven track record and
                customer-centric approach have made us a preferred partner in
                the industry.
              </p>
              <p>
                With a dedicated workforce of 700+ professionals, including
                engineers, supervisors, skilled and semi-skilled workers, we
                ensure smooth execution of projects across locations. At Ideas,
                quality, safety, and customer satisfaction are the cornerstones
                of every project we undertake. Our continuous focus on
                innovation, efficiency, and service excellence has helped us
                build a distinct identity and a reputation for reliability in
                the EPC sector.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Counter;